import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import defaultState, { requiredFields } from '@models/brandGroupProvider'
import type { BrandGroupModel, BrandGroupProviderModel, ProviderModel } from '@models/types'

import * as brandGroupProviderActions from '@redux/modules/brandGroupProvider'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

export type CreateBrandGroupProviderParams = {
  brandGroupProviderParams: Partial<BrandGroupProviderModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createBrandGroupProvider = (params: CreateBrandGroupProviderParams) => {
  const { brandGroupProviderParams, dispatch, requestOptions } = params
  const { createBrandGroupProvider: createFn } = brandGroupProviderActions

  return dispatch(createFn(brandGroupProviderParams, requestOptions))
}

export type DeleteBrandGroupProviderParams = {
  brandGroupProvider: Partial<BrandGroupProviderModel>,
  dispatch: AppDispatch,
}

const deleteBrandGroupProvider = (params: DeleteBrandGroupProviderParams) => {
  const { brandGroupProvider, dispatch } = params
  const { deleteBrandGroupProvider: deleteFn } = brandGroupProviderActions

  // @ts-expect-error TS2345
  return dispatch(deleteFn(brandGroupProvider))
}

export type ShowCreateBrandGroupProviderModalPayload = {
  brandGroupProvider: Partial<BrandGroupProviderModel>,
}

type CustomPayload = {
  brandId?: number,
}

type AddBrandGroupProviderParams = {
  brandGroupProvider: Partial<BrandGroupProviderModel>,
  customPayload?: CustomPayload,

  showCreateBrandGroupProviderModal: (payload: ShowCreateBrandGroupProviderModalPayload) => void,
}

const addBrandGroupProvider = (params: AddBrandGroupProviderParams) => new Promise((resolve, reject) => {
  const { brandGroupProvider, showCreateBrandGroupProviderModal, customPayload = {} } = params

  if (showCreateBrandGroupProviderModal){
    const payload = {
      brandGroupProvider,
      ...customPayload,
    }

    showCreateBrandGroupProviderModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateBrandGroupProviderModal not defined in PageContext callbacks'))
})

export function useBrandGroupProviderForm(
  brandGroupProvider: Partial<BrandGroupProviderModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const brandGroupProviderForm = useForm(
    defaultState,
    { entity: brandGroupProvider, requiredFields, validateOn },
    [brandGroupProvider.id, brandGroupProvider.cache_key],
  )

  return {
    ...brandGroupProviderForm,
  }
}

export function useRelations(brandGroupProvider: BrandGroupProviderModel) {
  const { brand_group_id, provider_id } = brandGroupProvider || {}

  const { brandGroups, providers } = useSelector(reduxState => reduxState.entities)

  const brandGroup: Partial<BrandGroupModel> = brandGroups[brand_group_id] || {}
  const provider: Partial<ProviderModel> = providers[provider_id] || {}

  return {
    brandGroup,
    provider,
  }
}

function useBrandGroupProvider(initEntity: Partial<BrandGroupProviderModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: brandGroupProvider } = useLatestEntity(initEntity, 'brandGroupProviders')

  const { callbacks } = useContext(PageContext)
  const {
    showCreateBrandGroupProviderModal,
  } = callbacks || {}

  const dispatch = useThunkDispatch()

  const {
    creating, deleting, loading,
  } = useSelector(reduxState => reduxState.brandGroupProviders)

  // @ts-expect-error TS2345
  const { brandGroup, provider } = useRelations(brandGroupProvider)

  return {
    brandGroup,
    brandGroupProvider,
    callbacks: {
      addBrandGroupProvider: (customPayload?: object) => (
        // @ts-expect-error TS2322
        addBrandGroupProvider({ brandGroupProvider, customPayload, showCreateBrandGroupProviderModal })
      ),
      createBrandGroupProvider: (
        brandGroupProviderParams: Partial<BrandGroupProviderModel>,
        requestOptions?: EntityOptions,
      ) => (createBrandGroupProvider({ brandGroupProviderParams, dispatch, requestOptions })),
      // @ts-expect-error TS2322
      deleteBrandGroupProvider: () => deleteBrandGroupProvider({ brandGroupProvider, dispatch }),
    },
    creating,
    deleting,
    loading,
    provider,
  }
}

export default useBrandGroupProvider
